import React, { useState } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { TextField, Chip } from '@material-ui/core';
import DropdownArrowMore from '@material-ui/icons/ExpandMore';
import { Autocomplete as MuiAutocomplete } from '@material-ui/lab';
import { createFilterOptions } from '@material-ui/lab/Autocomplete';
import Tooltip from '@material-ui/core/Tooltip';
import Checkbox from './Checkbox';
import Text from 'components/base/Text';
import { Flex } from 'components/base/layout-components';
import Icon from 'components/base/Icon';
import { getColor } from 'common/constants/_colors';
import { LINE_HEIGHT } from 'common/constants/sizes';

const limitFilterOptions = createFilterOptions({
  matchFrom: 'any',
  limit: 100
});

export const getFilterOptions = ({ term = '' } = {}) => {
  return term.trim() === ''
    ? limitFilterOptions
    : (searchOptions, { inputValue }) => {
        const results = searchOptions.filter(
          option =>
            option.isAlwaysVisible ||
            !option.searchableValue ||
            (option.searchableValue &&
              option.searchableValue.search(
                new RegExp(inputValue.replace(/[^a-zA-Z0-9 -.]/g, ''), 'i')
              ) !== -1) ||
            _.deburr(option.searchableValue)
              .toLowerCase()
              .includes(_.deburr(inputValue).toLowerCase())
        );
        return results.length > 100 ? results.slice(0, 100) : results;
      };
};

const DEFAULT_WIDTH = '20rem';

const Autocomplete = ({
  width = DEFAULT_WIDTH,
  height,
  chipInput,
  placeholder = 'Select option',
  options = [],
  multiple,
  multipleLimit,
  multiplePlaceholder,
  selectedOptionValue = null,
  disableClearable = true,
  disabled,
  tooltipTitle = 'Items selected',
  tooltipPlacment = 'right',
  disableTooltip = false,
  onChange,
  dataCy,
  itemsTooltipEnabled = false, // show  tooltip with full item value when hovering
  ...autoCompleteProps
}) => {
  const [hideTooltip, setHideTooltip] = useState(true);
  const [term, setTerm] = useState('');

  if (multiple && !selectedOptionValue) selectedOptionValue = [];

  if (multiple) {
    // quickfix: sync selectedOptionValue with options
    selectedOptionValue = selectedOptionValue.filter(value =>
      options.some(option => option.value === value)
    );
  }

  const tooltipContent = () => {
    if (!selectedOptionValue || !selectedOptionValue.length) return false;

    const title = `${selectedOptionValue.length} ${tooltipTitle}`;

    return (
      <Flex padding='x-small' direction='column' maxWidth='20rem'>
        <Text size='medium' weight='500'>
          {title}
        </Text>
        <Text
          size='medium'
          lineHeight={LINE_HEIGHT.medium}
          color='shuttleGray'
          pt='0.5rem'
        >
          {options
            .filter(({ value }) => selectedOptionValue.includes(value))
            .map(({ searchableValue }) => searchableValue)
            .join(', ')}
        </Text>
      </Flex>
    );
  };

  const autocompletePlaceholder = () => {
    if (!selectedOptionValue || (multiple && !selectedOptionValue.length))
      return placeholder;

    const selectedProjectName = _.get(
      _.find(options, option =>
        multiple
          ? option.value === selectedOptionValue[0]
          : option.value === selectedOptionValue
      ),
      'searchableValue',
      placeholder
    );

    if (!multiple || (multiple && selectedOptionValue.length === 1)) {
      return selectedProjectName;
    }

    if (multiple && selectedOptionValue.length > 1) {
      return multiplePlaceholder || `${selectedOptionValue.length} selected`;
    }
  };

  const autoCompleteDefaultProps = chipInput
    ? {
        freeSolo: true,
        onChange: (e, items) => {
          onChange(items ? items.map(item => item) : null);
        },
        renderTags: (value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              key={option}
              label={option}
              style={{
                margin: 0,
                marginLeft: '-5px',
                marginRight: '8px',
                fontSize: '1rem',
                height: '2.2rem',
                color: getColor('shark'),
                border: `1px solid ${getColor('botticelli')}`,
                backgroundColor: getColor('white')
              }}
              deleteIcon={
                <Icon
                  name='Close'
                  style={{ width: '1.2rem', color: getColor('nepal') }}
                />
              }
              {...getTagProps({ index })}
            />
          ))
      }
    : {
        inputValue: term,
        renderTags: () => null,
        getOptionLabel: () => '',
        getOptionSelected: (opt, val) => opt.value === val.value,
        filterOptions: getFilterOptions({ term }),
        onChange: (e, items) => {
          onChange(
            items
              ? multiple
                ? items.map(({ value }) => value)
                : items.value
              : null
          );
        },
        ...(multiple
          ? {
              value: selectedOptionValue
                ? selectedOptionValue.map(id => ({ value: id }))
                : [{ value: '' }]
            }
          : { value: '' })
      };

  const isMaxLimit =
    multiple && multipleLimit && _.size(selectedOptionValue) >= multipleLimit;

  return (
    <MuiAutocomplete
      {...autoCompleteDefaultProps}
      {...autoCompleteProps}
      onClose={() => {
        setTerm('');
      }}
      onKeyDown={event => event.stopPropagation()}
      disableCloseOnSelect={multiple}
      multiple={multiple}
      disableClearable={disableClearable}
      disabled={disabled}
      style={{ width, height }}
      popupIcon={<DropdownArrowMore />}
      options={options}
      data-cy={dataCy || 'autocomplete'}
      getOptionDisabled={option =>
        isMaxLimit && selectedOptionValue.indexOf(option.value) === -1
      }
      renderOption={option => (
        <Tooltip
          title={option.displayValue}
          disableHoverListener={!itemsTooltipEnabled}
        >
          <Flex items='center'>
            {multiple && (
              <Checkbox
                checked={selectedOptionValue.indexOf(option.value) > -1}
                data-cy={option.displayValue}
              />
            )}
            <div>{option.displayValue}</div>
          </Flex>
        </Tooltip>
      )}
      renderInput={params => (
        <Tooltip
          placement={tooltipPlacment}
          disableHoverListener={disableTooltip}
          title={tooltipContent()}
          onClose={() => setHideTooltip(true)}
          onOpen={() => setHideTooltip(false)}
          {...(!hideTooltip && multiple && selectedOptionValue.length > 1
            ? { open: true }
            : { open: false })}
        >
          <TextField
            {...params}
            onChange={e => setTerm(e.target.value)}
            variant='outlined'
            placeholder={autocompletePlaceholder()}
            fullWidth
          />
        </Tooltip>
      )}
    />
  );
};

Autocomplete.propTypes = {
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  width: PropTypes.string,
  multiple: PropTypes.bool,
  chipInput: PropTypes.bool,
  disabled: PropTypes.bool,
  disableClearable: PropTypes.bool,
  selectedOptionValue: PropTypes.any,
  dataCy: PropTypes.string
};

export default Autocomplete;
